import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, Cog6ToothIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import DesignTokens from '../../styles/DesignTokens';

const CONSENT_KEY = 'safarglimpse_cookie_consent';
const CONSENT_VERSION = '1.0'; // Increment when consent requirements change

const ConsentBanner = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.surface};
  border-top: 2px solid ${props => props.theme.colors.primary};
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  padding: ${DesignTokens.spacing.lg} ${DesignTokens.spacing.md};
  max-width: 100%;

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${DesignTokens.spacing.xl} ${DesignTokens.spacing['2xl']};
  }
`;

const ConsentContent = styled.div`
  max-width: ${DesignTokens.container.xl};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.md};

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const ConsentText = styled.div`
  flex: 1;
  font-size: ${DesignTokens.typography.fontSize.sm};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  color: ${props => props.theme.colors.textSecondary};

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize.base};
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
    
    &:hover {
      color: ${props => props.theme.colors.primaryHover};
    }
  }
`;

const ConsentActions = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.sm};
  flex-wrap: wrap;
  align-items: center;

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    flex-wrap: nowrap;
  }
`;

const ConsentButton = styled(motion.button)`
  padding: ${DesignTokens.spacing.sm} ${DesignTokens.spacing.lg};
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  cursor: pointer;
  border: none;
  transition: all ${DesignTokens.transition.normal} ease;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  white-space: nowrap;

  &.primary {
    background: ${props => props.theme.colors.primary};
    color: white;
    
    &:hover {
      background: ${props => props.theme.colors.primaryHover};
    }
  }

  &.secondary {
    background: ${props => props.theme.colors.surface};
    color: ${props => props.theme.colors.text};
    border: 1px solid ${props => props.theme.colors.border};
    
    &:hover {
      background: ${props => props.theme.colors.hover};
      border-color: ${props => props.theme.colors.primary};
    }
  }

  &.settings {
    background: transparent;
    color: ${props => props.theme.colors.textSecondary};
    border: 1px solid ${props => props.theme.colors.border};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const PreferencesModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${DesignTokens.spacing.md};
`;

const PreferencesContent = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${DesignTokens.borderRadius.lg};
  padding: ${DesignTokens.spacing['2xl']};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${DesignTokens.shadow.xl};
`;

const PreferencesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${DesignTokens.spacing.xl};
`;

const PreferencesTitle = styled.h2`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  padding: ${DesignTokens.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${DesignTokens.borderRadius.md};
  transition: all ${DesignTokens.transition.normal} ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text};
  }
`;

const PreferenceSection = styled.div`
  margin-bottom: ${DesignTokens.spacing.xl};
`;

const PreferenceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${DesignTokens.spacing.md};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.borderLight};
  border-radius: ${DesignTokens.borderRadius.md};
  margin-bottom: ${DesignTokens.spacing.md};
`;

const PreferenceInfo = styled.div`
  flex: 1;
  margin-right: ${DesignTokens.spacing.md};
`;

const PreferenceTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${DesignTokens.spacing.xs};
`;

const PreferenceDescription = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
`;

const Toggle = styled.button`
  width: 48px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all ${DesignTokens.transition.normal} ease;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.active ? '26px' : '2px'};
    transition: all ${DesignTokens.transition.normal} ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consent, setConsent] = useState({
    essential: true, // Always true, cannot be disabled
    functional: false,
    analytics: false
  });

  useEffect(() => {
    // Check if consent has been given
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    
    if (storedConsent) {
      try {
        const consentData = JSON.parse(storedConsent);
        // Check if consent version matches (if version changes, show banner again)
        if (consentData.version === CONSENT_VERSION) {
          setConsent(consentData.consent);
          setShowBanner(false);
          return;
        }
      } catch (e) {
        // Invalid stored consent, show banner
      }
    }
    
    // Show banner if no consent or version mismatch
    setShowBanner(true);
  }, []);

  const saveConsent = (newConsent) => {
    const consentData = {
      version: CONSENT_VERSION,
      consent: {
        ...newConsent,
        essential: true // Always keep essential as true
      },
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setConsent(consentData.consent);
    setShowBanner(false);
    setShowPreferences(false);
    
    // Trigger custom event for other components to listen
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: consentData.consent 
    }));
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      analytics: true
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      essential: true,
      functional: false,
      analytics: false
    });
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Essential cannot be toggled
    setConsent(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner && !showPreferences) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <ConsentBanner
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ConsentContent>
              <ConsentText>
                We use local storage and cookies to enhance your experience, analyze site usage, 
                and assist in our marketing efforts. By clicking "Accept All", you consent to our 
                use of cookies. You can manage your preferences at any time.{' '}
                <Link to="/privacy">Learn more in our Privacy Policy</Link>.
              </ConsentText>
              <ConsentActions>
                <ConsentButton
                  className="settings"
                  onClick={() => setShowPreferences(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Cog6ToothIcon style={{ width: '16px', height: '16px' }} />
                  Preferences
                </ConsentButton>
                <ConsentButton
                  className="secondary"
                  onClick={handleRejectAll}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Reject All
                </ConsentButton>
                <ConsentButton
                  className="primary"
                  onClick={handleAcceptAll}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                  Accept All
                </ConsentButton>
              </ConsentActions>
            </ConsentContent>
          </ConsentBanner>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPreferences && (
          <PreferencesModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPreferences(false)}
          >
            <PreferencesContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <PreferencesHeader>
                <PreferencesTitle>Cookie Preferences</PreferencesTitle>
                <CloseButton onClick={() => setShowPreferences(false)}>
                  <XMarkIcon style={{ width: '24px', height: '24px' }} />
                </CloseButton>
              </PreferencesHeader>

              <PreferenceSection>
                <PreferenceItem>
                  <PreferenceInfo>
                    <PreferenceTitle>Essential Cookies</PreferenceTitle>
                    <PreferenceDescription>
                      Required for the website to function properly. These cannot be disabled as they 
                      are necessary for core functionality like user ID tracking for itinerary limits.
                    </PreferenceDescription>
                  </PreferenceInfo>
                  <Toggle active={true} disabled />
                </PreferenceItem>

                <PreferenceItem>
                  <PreferenceInfo>
                    <PreferenceTitle>Functional Cookies</PreferenceTitle>
                    <PreferenceDescription>
                      Enable enhanced functionality like remembering your game progress, preferences, 
                      and geolocation for personalized content.
                    </PreferenceDescription>
                  </PreferenceInfo>
                  <Toggle 
                    active={consent.functional} 
                    onClick={() => togglePreference('functional')}
                  />
                </PreferenceItem>

                <PreferenceItem>
                  <PreferenceInfo>
                    <PreferenceTitle>Analytics Cookies</PreferenceTitle>
                    <PreferenceDescription>
                      Help us understand how visitors interact with our website by collecting and 
                      reporting information anonymously. Currently not implemented but reserved for 
                      future analytics integration.
                    </PreferenceDescription>
                  </PreferenceInfo>
                  <Toggle 
                    active={consent.analytics} 
                    onClick={() => togglePreference('analytics')}
                  />
                </PreferenceItem>
              </PreferenceSection>

              <ConsentActions style={{ marginTop: DesignTokens.spacing.xl }}>
                <ConsentButton
                  className="secondary"
                  onClick={() => setShowPreferences(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </ConsentButton>
                <ConsentButton
                  className="primary"
                  onClick={handleSavePreferences}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Save Preferences
                </ConsentButton>
              </ConsentActions>
            </PreferencesContent>
          </PreferencesModal>
        )}
      </AnimatePresence>
    </>
  );
}

export default CookieConsent;

// Utility function to check if consent has been given
export function hasConsent(type = 'essential') {
  try {
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    if (!storedConsent) return type === 'essential'; // Essential is always allowed
    
    const consentData = JSON.parse(storedConsent);
    if (consentData.version !== CONSENT_VERSION) return type === 'essential';
    
    return consentData.consent[type] === true;
  } catch (e) {
    return type === 'essential'; // Default to essential only
  }
}

// Utility function to get all consent preferences
export function getConsentPreferences() {
  try {
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    if (!storedConsent) {
      return { essential: true, functional: false, analytics: false };
    }
    
    const consentData = JSON.parse(storedConsent);
    if (consentData.version !== CONSENT_VERSION) {
      return { essential: true, functional: false, analytics: false };
    }
    
    return consentData.consent;
  } catch (e) {
    return { essential: true, functional: false, analytics: false };
  }
}

